import { Component, input, computed } from '@angular/core';

@Component({
  selector: 'app-badge',
  standalone: true,
  template: `
    <span
      class="badge"
      title="{{ title() }}"
      [class]="badgeClassToShow()"
      [class.is-live-status]="isLiveStatus()"
      [class.is-processing-status]="isProcessingStatus()"
      [attr.aria-label]="'Status: ' + label()"
    >
      @if (isLiveStatus()) {
        <span class="badge-live-dot" aria-hidden="true"></span>
      }
      @if (hasIconToShow()) {
        <i class="bi" [class]="iconToShow()" aria-hidden="true"></i>
      }
      @if(label()){
        <span class="badge-text">{{ label() }}</span>
      }
    </span>
  `,
  styles: `
    @use 'sass:map';
    @use 'variables' as *;
    @use 'mixins' as *;

    :host{
      @include flex(row, flex-start, center, nowrap, $space-1);
    }

    .badge {
      @include text-truncate(1);
      @include flex(row, flex-start, center, nowrap, $space-1);
      display: inline-flex;
      position: relative;
      padding: 0.1rem 0.6rem;
      border-radius: $radius-full;
      font-size: 0.7rem;
      font-weight: $font-weight-medium;
      text-transform: none;
      letter-spacing: 0.01em;
      border: 1px solid transparent;
      white-space: nowrap;
      transition: $transition-fast;
      line-height: 1.25;

      i { 
        font-size: 0.75rem;
        margin-right: 0.1rem;
      }

      .badge-live-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: currentColor;
        margin-right: 0.15rem;
        flex-shrink: 0;
        box-shadow: 0 0 0 0 rgba($color-warning, 0.55);
      }

      .badge-text {
        font-weight: $font-weight-medium;
      }

      // Semantic Variants
      &.badge-primary { background: rgba($color-primary, 0.1); color: $color-primary; border-color: rgba($color-primary, 0.05); }
      &.badge-success { background: rgba(map.get($colors, 'success', 'base'), 0.1); color: map.get($colors, 'success', 'base'); border-color: rgba(map.get($colors, 'success', 'base'), 0.05); }
      &.badge-error { background: rgba(map.get($colors, 'error', 'base'), 0.1); color: map.get($colors, 'error', 'base'); border-color: rgba(map.get($colors, 'error', 'base'), 0.05); }
      &.badge-warning { background: rgba(map.get($colors, 'warning', 'base'), 0.1); color: map.get($colors, 'warning', 'base'); border-color: rgba(map.get($colors, 'warning', 'base'), 0.05); }
      &.badge-info { background: rgba(map.get($colors, 'info', 'base'), 0.1); color: map.get($colors, 'info', 'base'); border-color: rgba(map.get($colors, 'info', 'base'), 0.05); }
      &.badge-secondary { background: map.get($colors, 'gray', '100'); color: map.get($colors, 'gray', '500'); border-color: map.get($colors, 'gray', '200'); }

      &.badge-motoboy { background: rgba(map.get($colors, 'info', 'base'), 0.1); color: map.get($colors, 'info', 'base'); border-color: rgba(map.get($colors, 'info', 'base'), 0.05); }
      &.badge-sedex { background: rgba(map.get($colors, 'yellow', 'base'), 0.1); color: map.get($colors, 'yellow', 'base'); border-color: rgba(map.get($colors, 'yellow', 'base'), 0.05); }
      
      &.badge-purple { background: rgba(#6b21a8, 0.1); color: #6b21a8; border-color: rgba(#6b21a8, 0.05); }
      &.badge-indigo { background: rgba(#3730a3, 0.1); color: #3730a3; border-color: rgba(#3730a3, 0.05); }
      &.badge-teal { background: rgba(#0d9488, 0.1); color: #0d9488; border-color: rgba(#0d9488, 0.05); }
      &.badge-admin { background: rgba(#28a745, 0.1); color: #28a745; border-color: rgba(#28a745, 0.05); }
      &.badge-yellow { background: rgba(#e6a22e, 0.1); color: #e6a22e; border-color: rgba(#e6a22e, 0.05); }
      &.badge-muted { background: rgba(map.get($colors, 'gray', '500'), 0.1); color: map.get($colors, 'gray', '500'); border-color: rgba(map.get($colors, 'gray', '500'), 0.05); }

      &.is-live-status {
        .badge-live-dot {
          animation: liveDotPulse 1.5s ease-out infinite;
        }
      }

      &.is-processing-status i {
        animation: softSpin 2.2s linear infinite;
      }
    }

    .spin {
      animation: spin 2s linear infinite;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes badgeDotPulse {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.7;
        transform: scale(1.14);
      }
    }

    @keyframes liveDotPulse {
      0% {
        box-shadow: 0 0 0 0 rgba($color-warning, 0.5);
      }
      75% {
        box-shadow: 0 0 0 7px rgba($color-warning, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba($color-warning, 0);
      }
    }

    @keyframes softSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
      .badge.is-live-status .badge-live-dot,
      .badge.is-processing-status i {
        animation: none;
      }
    }
  `,
})
export class BadgeComponent {
  icon = input<string>('');
  label = input<string>('');
  title = input<string>('');

  type = input<string>('primary');

  private readonly normalizedLabel = computed(() =>
    String(this.label() || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim(),
  );
  private readonly normalizedType = computed(() =>
    String(this.type() || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim(),
  );
  readonly hasIconToShow = computed(() => String(this.iconToShow() || '').trim().length > 0);
  private readonly resolvedStatusKey = computed(() => {
    const type = this.normalizedType();
    const label = this.normalizedLabel();

    const genericTypes = new Set([
      'PRIMARY',
      'SECONDARY',
      'SUCCESS',
      'ERROR',
      'DANGER',
      'WARNING',
      'INFO',
      'ADMIN',
      'PURPLE',
      'TEAL',
      'INDIGO',
      'YELLOW',
      'MUTED',
    ]);

    if (type && !genericTypes.has(type)) {
      return type;
    }

    const normalizedLabel = label.replace(/\s+/g, '_');
    const labelToStatusMap: Record<string, string> = {
      AGENDADO: 'AGENDADO',
      CONFIRMADO: 'CONFIRMADO',
      EM_ATENDIMENTO: 'EM_ATENDIMENTO',
      REALIZADO: 'CONCLUIDO',
      CONCLUIDO: 'CONCLUIDO',
      CANCELADO: 'CANCELADO',
      CRIADO: 'CRIADO',
      PENDENTE: 'PENDENTE',
      FINALIZADO: 'FINALIZADO',
      FINALIZADA: 'FINALIZADA',
      EM_ANDAMENTO: 'EM_ANDAMENTO',
      RASCUNHO: 'RASCUNHO',
      EM_REVISAO: 'EM_REVISAO',
      A_SER_PREENCHIDO: 'A_SER_PREENCHIDO',
      ERRO_VALIDACAO: 'ERRO_VALIDACAO',
      PREENCHIDO: 'PREENCHIDO',
      CONCLUIDA: 'CONCLUIDA',
      PROCESSADA: 'PROCESSADA',
      REJEITADA: 'REJEITADA',
      EM_PROCESSAMENTO: 'EM_PROCESSAMENTO',
      ATIVO: 'ATIVO',
      INATIVO: 'INATIVO',
    };

    return labelToStatusMap[normalizedLabel] ?? (type || 'PRIMARY');
  });

  isLiveStatus = computed(() => {
    const rawLabel = this.normalizedLabel();
    const rawType = this.normalizedType();

    return (
      rawLabel.includes('EM ATENDIMENTO') ||
      rawLabel.includes('EM ANDAMENTO') ||
      rawLabel.includes('PROCESSANDO') ||
      rawLabel.includes('EM PROCESSAMENTO') ||
      rawType === 'EM_ATENDIMENTO' ||
      rawType === 'EM_ANDAMENTO' ||
      rawType === 'EM_PROCESSAMENTO' ||
      rawType === 'PROCESSANDO'
    );
  });

  isProcessingStatus = computed(() => {
    const rawLabel = this.normalizedLabel();
    const rawType = this.normalizedType();
    return (
      rawLabel.includes('PROCESSANDO') ||
      rawLabel.includes('EM PROCESSAMENTO') ||
      rawType === 'EM_PROCESSAMENTO' ||
      rawType === 'PROCESSANDO'
    );
  });

  badgeClassToShow = computed(() => {
    const type = this.resolvedStatusKey();

    const badgeClassMap: Record<string, string> = {
      // Base & Generic
      PRIMARY: 'badge-primary',
      SECONDARY: 'badge-secondary',
      SUCCESS: 'badge-success',
      ERROR: 'badge-error',
      DANGER: 'badge-error',
      WARNING: 'badge-warning',
      INFO: 'badge-info',
      ADMIN: 'badge-admin',
      PURPLE: 'badge-purple',
      TEAL: 'badge-teal',
      INDIGO: 'badge-indigo',
      YELLOW: 'badge-yellow',
      MUTED: 'badge-muted',

      // OBJETO
      CRIADO: 'badge-secondary',
      PROCESSADO: 'badge-purple',
      EMITIDO: 'badge-info',
      POSTADO: 'badge-primary',
      CANCELADO: 'badge-error',
      DEVOLVIDO: 'badge-warning',
      ENTREGUE: 'badge-success',

      // SERVICO
      SEDEX: 'badge-sedex',
      MOTOBOY: 'badge-motoboy',

      // ITEM
      RASTREIO: 'badge-primary',
      OBJECTS: 'badge-primary',

      // FATURA
      PENDENTE: 'badge-secondary',
      CONCILIADO: 'badge-success',
      CONCILIADA: 'badge-success',

      // REMESSA
      CRIADA: 'badge-indigo',
      IMPORTADA: 'badge-indigo',
      EM_PROCESSAMENTO: 'badge-warning',
      REJEITADA: 'badge-error',
      PROCESSADA: 'badge-purple',
      POSTADA: 'badge-primary',
      CANCELADA: 'badge-error',
      CONCLUIDA: 'badge-success',

      // SERVICO & CENTRO_CUSTO
      ATIVO: 'badge-success',
      INATIVO: 'badge-secondary',

      // Rastreio (Legado/Suporte)
      EM_TRANSITO: 'badge-warning',
      EM_TRÂNSITO: 'badge-warning',
      AGUARDANDO: 'badge-info'
    };
    return badgeClassMap[type] || 'badge-primary';
  });

  iconToShow = computed(() => {
    if (this.icon()) return this.icon();

    const type = this.resolvedStatusKey();
    const iconMap: Record<string, string> = {
      // Base
      PRIMARY: 'bi-circle-fill',
      SECONDARY: 'bi-dash-circle',
      SUCCESS: 'bi-check-circle-fill',
      ERROR: 'bi-x-circle-fill',
      DANGER: 'bi-x-circle-fill',
      WARNING: 'bi-exclamation-triangle-fill',
      INFO: 'bi-info-circle-fill',
      ADMIN: 'bi-shield-lock-fill',
      PURPLE: 'bi-polygon-fill',
      TEAL: 'bi-people-fill',
      INDIGO: 'bi-people-fill',
      YELLOW: 'bi-people-fill',
      MUTED: 'bi-exclamation-circle',

      // OBJETO
      CRIADO: 'bi-plus-circle',
      PROCESSADO: 'bi-gear-wide-connected',
      EMITIDO: 'bi-file-earmark-check',
      POSTADO: 'bi-send-check-fill',
      CANCELADO: 'bi-x-octagon-fill',
      DEVOLVIDO: 'bi-arrow-return-left',
      ENTREGUE: 'bi-house-check-fill',

      // SERVICO
      SEDEX: 'bi-truck',
      MOTOBOY: 'bi-bicycle',

      // ITEM 
      RASTREIO: 'bi-truck',
      OBJECTS: 'bi-stack',

      // FATURA
      PENDENTE: 'bi-clock-history',
      CONCILIADO: 'bi-check-all',
      CONCILIADA: 'bi-check-all',

      // REMESSA
      CRIADA: 'bi-plus-circle',
      IMPORTADA: 'bi-download',
      EM_PROCESSAMENTO: 'bi-arrow-repeat spin',
      REJEITADA: 'bi-exclamation-octagon',
      PROCESSADA: 'bi-gear-wide-connected',
      POSTADA: 'bi-send-check-fill',
      CANCELADA: 'bi-x-octagon-fill',
      CONCLUIDA: 'bi-check-circle-fill',

      // SERVICO & CENTRO_CUSTO
      ATIVO: 'bi-check-lg',
      INATIVO: 'bi-slash-circle',

      // Rastreio (Legado/Suporte)
      EM_TRANSITO: 'bi-truck',
      EM_TRÂNSITO: 'bi-truck',
      AGUARDANDO: 'bi-clock'

    };
    return iconMap[type] || '';
  });
}
